import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Route, Routes, useNavigate, useParams } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import { startClubSupportChat } from "../../../utils/platformSupport";
import SupportConversationPage from "../../common/SupportChat/SupportConversationPage";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import PlatformPageHeader from "./PlatformPageHeader";
import { ReadTicks } from "../../Clinic/common/ChatThread";
import cs from "../../Clinic/common/chat.module.css";
import s from "./platformAdmin.module.css";

function formatPreviewTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function WriteClubButton({ onClick, compact = false }) {
  return (
    <button
      type="button"
      className={compact ? s.supportWriteBtn : s.heroActionBtn}
      onClick={onClick}
    >
      Написать клубу
    </button>
  );
}

function ClubPickerModal({ onClose }) {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    platformAPI
      .getClinics()
      .then((data) => {
        if (!cancelled) setClinics(data.clinics || []);
      })
      .catch((e) => {
        if (!cancelled) {
          setErr(e.response?.data?.error || "Не удалось загрузить клубы");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clinics;
    return clinics.filter((club) => {
      const hay = [
        club.name,
        club.subdomain,
        club.owner?.full_name,
        club.owner?.email,
        club.owner?.phone,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [clinics, query]);

  const openClub = async (tenantId) => {
    setErr("");
    setBusyId(tenantId);
    try {
      const started = await startClubSupportChat(tenantId);
      onClose();
      navigate(started.path);
    } catch (e) {
      setErr(e.response?.data?.error || "Не удалось открыть чат");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div
      className={s.supportComposeOverlay}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={s.supportComposeSheet}
        role="dialog"
        aria-labelledby="support-compose-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={s.supportComposeHead}>
          <div>
            <h2 id="support-compose-title" className={s.exportPanelTitle}>
              Написать клубу
            </h2>
            <p className={s.exportPanelLead}>
              Сообщение увидит владелец в чате поддержки клуба.
            </p>
          </div>
          <button
            type="button"
            className={s.heroActionBtnGhost}
            onClick={onClose}
          >
            Закрыть
          </button>
        </div>

        <input
          className={s.clubSearch}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Клуб, владелец или email"
          autoFocus
        />

        {err ? <div className={s.alertError}>{err}</div> : null}

        {loading ? (
          <p className={s.stateMuted}>Загрузка клубов…</p>
        ) : filtered.length ? (
          <ul className={s.supportComposeList}>
            {filtered.map((club) => (
              <li key={club.id}>
                <button
                  type="button"
                  className={s.supportComposeRow}
                  disabled={Boolean(busyId)}
                  onClick={() => openClub(club.id)}
                >
                  <span className={s.supportComposeName}>{club.name}</span>
                  <span className={s.supportComposeMeta}>
                    {club.owner?.full_name || club.owner?.email || club.subdomain}
                    {busyId === club.id ? " · открываем…" : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className={s.supportEmptyHint}>Клубов не найдено</p>
        )}
      </div>
    </div>
  );
}

function PlatformSupportInbox({ onWriteClub }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await platformAPI.listSupportThreads();
      setThreads(data.threads || []);
    } catch {
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 3000);
    const onActivity = () => load();
    window.addEventListener("baysport:support-activity", onActivity);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("baysport:support-activity", onActivity);
    };
  }, [load]);

  if (loading) {
    return <p className={s.stateMuted}>Загрузка…</p>;
  }

  if (!threads.length) {
    return (
      <div className={s.supportEmpty}>
        <p className={s.supportEmptyTitle}>Пока нет переписок</p>
        <p className={s.supportEmptyHint}>
          Можно написать клубу первым — ждать их сообщение не нужно.
        </p>
        <div className={s.supportEmptyActions}>
          <WriteClubButton onClick={onWriteClub} />
        </div>
      </div>
    );
  }

  return (
    <ul className={`${cs.chatInboxList} ${s.platformInboxList}`}>
      {threads.map((thread) => (
        <li key={thread.thread_key}>
          <NavLink
            to={`/admin/support/${encodeURIComponent(thread.thread_key)}`}
            className={({ isActive }) =>
              isActive
                ? `${cs.chatInboxRow} ${cs.chatInboxRowActive}`
                : cs.chatInboxRow
            }
          >
            <span className={cs.chatInboxAvatarClub} aria-hidden>
              {thread.channel === "owner" ? "🙋" : "🏟"}
            </span>
            <span className={cs.chatInboxBody}>
              <span className={cs.chatInboxTop}>
                <span className={cs.chatInboxName}>{thread.title}</span>
                <span className={cs.chatInboxTime}>
                  {formatPreviewTime(thread.last_at)}
                </span>
              </span>
              <span className={cs.chatInboxPreviewRow}>
                {thread.last_is_mine ? (
                  <ReadTicks read={Boolean(thread.last_is_read)} />
                ) : null}
                <span className={cs.chatInboxPreview}>
                  {thread.subtitle}
                  {thread.last_body ? ` · ${thread.last_body}` : ""}
                </span>
              </span>
            </span>
            {thread.unread_count > 0 ? (
              <span className={cs.chatInboxBadge}>
                {thread.unread_count > 99 ? "99+" : thread.unread_count}
              </span>
            ) : null}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function PlatformSupportThread({ onWriteClub }) {
  const { threadKey } = useParams();
  const decodedKey = decodeURIComponent(threadKey || "");
  const [meta, setMeta] = useState(null);
  const isMobile = useIsMobile(900);

  if (isMobile) {
    return (
      <SupportConversationPage
        mode="platform"
        threadKey={decodedKey}
        title={meta?.title || "Обращение"}
        subtitle={
          meta?.club_name
            ? `${meta.club_name}${meta.user_role ? ` · ${meta.user_role}` : ""}`
            : meta?.subtitle || "Поддержка"
        }
        emptyTitle="Напишите клубу"
        emptyHint="Сообщение появится у владельца в чате поддержки."
        fullscreen
        backTo="/admin/support"
        onMeta={setMeta}
      />
    );
  }

  return (
    <div className={s.supportThreadLayout}>
      <div className={s.supportThreadSide}>
        <div className={s.supportThreadSideHead}>
          <span className={s.supportSideTitle}>Обращения</span>
          <WriteClubButton compact onClick={onWriteClub} />
        </div>
        <PlatformSupportInbox onWriteClub={onWriteClub} />
      </div>
      <div className={s.supportThreadMain}>
        <SupportConversationPage
          mode="platform"
          threadKey={decodedKey}
          title={meta?.title || "Обращение"}
          subtitle={
            meta?.club_name
              ? `${meta.club_name}${meta.user_role ? ` · ${meta.user_role}` : ""}`
              : meta?.subtitle || "Поддержка"
          }
          emptyTitle="Напишите клубу"
          emptyHint="Сообщение появится у владельца в чате поддержки."
          fullscreen={false}
          hideBack
          onMeta={setMeta}
        />
      </div>
    </div>
  );
}

export default function PlatformSupport() {
  const isMobile = useIsMobile(900);
  const [composeOpen, setComposeOpen] = useState(false);
  const openCompose = () => setComposeOpen(true);
  const closeCompose = () => setComposeOpen(false);

  return (
    <div className={`${s.supportPage} ${isMobile ? s.supportPageMobile : ""}`}>
      {!isMobile ? (
        <PlatformPageHeader
          title="Поддержка"
          lead="Можно написать клубу первым — ждать их сообщение не нужно."
          actions={<WriteClubButton onClick={openCompose} />}
        />
      ) : null}
      <Routes>
        <Route
          index
          element={
            <div className={s.supportInboxOnly}>
              {isMobile ? (
                <header className={s.appHubHead}>
                  <div className={s.supportHubHeadRow}>
                    <h1 className={s.appHubTitle}>Поддержка</h1>
                    <WriteClubButton compact onClick={openCompose} />
                  </div>
                </header>
              ) : null}
              <PlatformSupportInbox onWriteClub={openCompose} />
            </div>
          }
        />
        <Route
          path=":threadKey"
          element={<PlatformSupportThread onWriteClub={openCompose} />}
        />
      </Routes>
      {composeOpen ? <ClubPickerModal onClose={closeCompose} /> : null}
    </div>
  );
}
